import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  LockKeyhole,
  ExternalLink,
  Scale,
  ChevronRight,
} from "lucide-react";

// ============================================================
// LEGAL TABS
// ============================================================

const TABS = [
  {
    id: "disclaimer",
    label: "Disclaimer",
    mobileLabel: "Disclaimer",
    icon: ShieldCheck,
  },
  {
    id: "terms",
    label: "Terms & Conditions",
    mobileLabel: "Terms",
    icon: FileText,
  },
  {
    id: "privacy",
    label: "Privacy Policy",
    mobileLabel: "Privacy",
    icon: LockKeyhole,
  },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function Legal() {
  const [active, setActive] = useState("disclaimer");

  const activeTab = TABS.find((tab) => tab.id === active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 py-6 sm:py-8 lg:py-10 px-3 sm:px-5">
      <div className="w-full max-w-6xl mx-auto">
        {/* ========================================================
            PAGE HEADER
        ======================================================== */}

        <div className="text-center mb-6 sm:mb-8">
          {/* Legal Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-medium mb-4">
            <Scale className="w-4 h-4" />
            Legal & Compliance
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Legal Information
          </h1>

          {/* Description */}
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-slate-600 leading-relaxed">
            Please review the legal information, terms, and privacy practices
            that apply when using the MRHealth platform.
          </p>

          {/* Updated Date */}
          {/* <p className="mt-2 text-xs sm:text-sm text-slate-500">
            Last Updated: August 30, 2026
          </p> */}
        </div>

        {/* ========================================================
            MAIN CARD
        ======================================================== */}

        <div className="bg-white/90 backdrop-blur-xl border border-slate-200/80 shadow-xl shadow-slate-200/40 rounded-2xl sm:rounded-3xl overflow-hidden">
          {/* ======================================================
              TAB NAVIGATION
          ====================================================== */}

          <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-xl border-b border-slate-200">
            <div className="p-2 sm:p-3">
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 max-w-3xl mx-auto">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = active === tab.id;

                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActive(tab.id)}
                      aria-selected={isActive}
                      role="tab"
                      className={`
                        relative flex items-center justify-center
                        gap-1.5 sm:gap-2
                        px-2 sm:px-4
                        py-2.5 sm:py-3
                        rounded-xl sm:rounded-2xl
                        text-xs sm:text-sm
                        font-semibold
                        transition-all duration-200
                        focus:outline-none
                        focus-visible:ring-2
                        focus-visible:ring-blue-500
                        focus-visible:ring-offset-2
                        ${
                          isActive
                            ? "text-white shadow-md"
                            : "text-slate-600 hover:text-blue-700 hover:bg-slate-50"
                        }
                      `}
                    >
                      {/* Active Background */}
                      {isActive && (
                        <motion.div
                          layoutId="activeLegalTab"
                          className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500"
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 30,
                          }}
                        />
                      )}

                      {/* Tab Content */}
                      <span className="relative z-10 flex items-center gap-1.5 sm:gap-2">
                        <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />

                        {/* Desktop */}
                        <span className="hidden sm:inline">{tab.label}</span>

                        {/* Mobile */}
                        <span className="sm:hidden">{tab.mobileLabel}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ======================================================
              CONTENT
          ====================================================== */}

          <div className="p-5 sm:p-8 lg:p-12">
            {/* Breadcrumb */}

            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 mb-6">
              <span>Legal</span>

              <ChevronRight className="w-3.5 h-3.5" />

              <span className="text-blue-600 font-medium">
                {activeTab?.label}
              </span>
            </div>

            {/* ====================================================
                ANIMATED CONTENT
            ==================================================== */}

            <AnimatePresence mode="wait">
              {/* ==================================================
                  DISCLAIMER
              ================================================== */}

              {active === "disclaimer" && (
                <motion.section
                  key="disclaimer"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-7"
                >
                  <SectionHeader
                    icon={ShieldCheck}
                    title="Disclaimer"
                    description="Important information about the use of the MRHealth platform."
                  />

                  {/* General Information */}

                  <LegalSection title="General Information">
                    <p>
                      MRHealth is a digital healthcare platform designed to
                      connect patients with healthcare professionals, healthcare
                      services, laboratories, and other healthcare ecosystem
                      participants.
                    </p>

                    <p>
                      The information available through MRHealth is provided for
                      general informational and platform-service purposes. It
                      should not be considered a substitute for professional
                      medical advice, diagnosis, treatment, or emergency medical
                      care.
                    </p>
                  </LegalSection>

                  {/* Healthcare Professionals */}

                  <LegalSection title="Healthcare Professionals">
                    <p>
                      Healthcare professionals listed on the platform are
                      expected to provide accurate professional and registration
                      information. Where applicable, professional registration
                      numbers may be displayed on their profiles for
                      transparency and verification purposes.
                    </p>

                    <p>
                      Users are encouraged to independently verify the
                      professional credentials and registration details of a
                      healthcare provider before relying on their services.
                    </p>

                    <a
                      href="https://www.nmc.org.in/information-desk/indian-medical-register/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-2 px-4 py-2.5 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium text-sm transition-colors"
                    >
                      Verify through NMC Indian Medical Register
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </LegalSection>

                  {/* No Medical Advice */}

                  <LegalSection title="No Medical Advice">
                    <p>
                      Content available on MRHealth, including profiles,
                      descriptions, educational information, appointment
                      information, and other platform content, does not
                      constitute medical advice.
                    </p>

                    <p>
                      Patients should consult a qualified healthcare
                      professional for medical conditions, diagnosis, treatment,
                      medication, or other healthcare decisions.
                    </p>
                  </LegalSection>

                  {/* Emergency */}

                  <LegalSection title="Emergency Situations">
                    <p>
                      MRHealth should not be used as a replacement for emergency
                      medical services. In an emergency, users should
                      immediately contact the appropriate emergency medical
                      services or visit the nearest emergency healthcare
                      facility.
                    </p>
                  </LegalSection>

                  {/* Accuracy */}

                  <LegalSection title="Accuracy of Information">
                    <p>
                      MRHealth makes reasonable efforts to maintain accurate and
                      useful information. However, we do not guarantee that all
                      information available through the platform will always be
                      complete, current, accurate, or error-free.
                    </p>
                  </LegalSection>

                  {/* Limitation */}

                  <LegalSection title="Limitation of Responsibility">
                    <p>
                      To the extent permitted by applicable law, MRHealth, its
                      owners, employees, representatives, and service providers
                      shall not be responsible for medical decisions, treatment
                      outcomes, professional conduct, or actions taken by users
                      or healthcare providers based solely on information
                      available through the platform.
                    </p>
                  </LegalSection>

                  {/* Notice */}

                  <LegalNotice>
                    By accessing or using MRHealth, you acknowledge that you
                    have read, understood, and accepted this disclaimer.
                  </LegalNotice>
                </motion.section>
              )}

              {/* ==================================================
                  TERMS & CONDITIONS
              ================================================== */}

              {active === "terms" && (
                <motion.section
                  key="terms"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-7"
                >
                  <SectionHeader
                    icon={FileText}
                    title="Terms & Conditions"
                    description="Rules and conditions governing your use of MRHealth."
                  />

                  {/* 1 */}

                  <LegalSection title="1. Acceptance of Terms">
                    <p>
                      By accessing, registering with, or using the MRHealth
                      platform, you agree to comply with these Terms &
                      Conditions and all applicable laws and regulations.
                    </p>

                    <p>
                      If you do not agree with these terms, you should not use
                      the platform.
                    </p>
                  </LegalSection>

                  {/* 2 */}

                  <LegalSection title="2. User Account">
                    <ul>
                      <li>
                        Users must provide accurate, complete, and current
                        information.
                      </li>

                      <li>
                        Users are responsible for maintaining the
                        confidentiality of their account credentials.
                      </li>

                      <li>
                        Users must immediately notify MRHealth of unauthorized
                        account access.
                      </li>

                      <li>
                        Users must not create accounts using false or misleading
                        information.
                      </li>
                    </ul>
                  </LegalSection>

                  {/* 3 */}

                  <LegalSection title="3. Healthcare Provider Information">
                    <p>
                      Healthcare providers are responsible for the accuracy of
                      their professional information, registration details,
                      availability, services, fees, and other information
                      displayed on their profiles.
                    </p>

                    <p>
                      Listing a healthcare provider on MRHealth does not
                      constitute a guarantee, endorsement, or warranty regarding
                      the provider's professional performance or treatment
                      outcomes.
                    </p>
                  </LegalSection>

                  {/* 4 */}

                  <LegalSection title="4. Appointments and Services">
                    <p>
                      Appointment availability, cancellations, rescheduling,
                      consultation terms, and service charges may depend on the
                      individual healthcare provider or service provider.
                    </p>

                    <p>
                      Users should carefully review appointment details before
                      confirming a booking.
                    </p>
                  </LegalSection>

                  {/* 5 */}

                  <LegalSection title="5. Prohibited Activities">
                    <ul>
                      <li>
                        Using the platform for fraudulent, unlawful, or
                        malicious purposes.
                      </li>

                      <li>
                        Attempting to gain unauthorized access to accounts,
                        servers, APIs, or platform systems.
                      </li>

                      <li>
                        Uploading malicious software, harmful code, or
                        unauthorized content.
                      </li>

                      <li>
                        Impersonating another person or healthcare professional.
                      </li>

                      <li>
                        Misusing, copying, scraping, or redistributing platform
                        information without authorization.
                      </li>
                    </ul>
                  </LegalSection>

                  {/* 6 */}

                  <LegalSection title="6. Intellectual Property">
                    <p>
                      Unless otherwise stated, the MRHealth name, branding,
                      logos, software, design, content, graphics, and other
                      platform materials are protected by applicable
                      intellectual-property laws.
                    </p>
                  </LegalSection>

                  {/* 7 */}

                  <LegalSection title="7. Platform Availability">
                    <p>
                      We may modify, suspend, restrict, or discontinue portions
                      of the platform when necessary for maintenance, security,
                      development, or operational reasons.
                    </p>
                  </LegalSection>

                  {/* 8 */}

                  <LegalSection title="8. Account Suspension or Termination">
                    <p>
                      MRHealth may suspend or terminate access where there is
                      reasonable evidence of violation of these terms, misuse of
                      the platform, fraudulent activity, security concerns, or
                      where required by applicable law.
                    </p>
                  </LegalSection>

                  {/* 9 */}

                  <LegalSection title="9. Changes to These Terms">
                    <p>
                      These Terms & Conditions may be updated periodically.
                      Updated terms become effective when published on the
                      platform unless otherwise stated.
                    </p>
                  </LegalSection>

                  {/* 10 */}

                  <LegalSection title="10. Governing Law">
                    <p>
                      These terms shall be interpreted and governed in
                      accordance with applicable laws and regulations of India,
                      subject to the jurisdiction of the appropriate courts.
                    </p>
                  </LegalSection>

                  <LegalNotice>
                    Continued use of MRHealth after changes to these terms
                    constitutes acceptance of the updated Terms & Conditions.
                  </LegalNotice>
                </motion.section>
              )}

              {/* ==================================================
                  PRIVACY POLICY
              ================================================== */}

              {active === "privacy" && (
                <motion.section
                  key="privacy"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-7"
                >
                  <SectionHeader
                    icon={LockKeyhole}
                    title="Privacy Policy"
                    description="How MRHealth collects, uses, protects, and handles information."
                  />

                  {/* 1 */}

                  <LegalSection title="1. Introduction">
                    <p>
                      This Privacy Policy explains how MRHealth may collect,
                      use, store, protect, and process information when you
                      access or use our platform and services.
                    </p>

                    <p>
                      By using MRHealth, you acknowledge the practices described
                      in this Privacy Policy.
                    </p>
                  </LegalSection>

                  {/* 2 */}

                  <LegalSection title="2. Information We May Collect">
                    <p>
                      Depending on how you use the platform, we may collect:
                    </p>

                    <ul>
                      <li>
                        Basic account information such as name and contact
                        details.
                      </li>

                      <li>
                        Account credentials and authentication information.
                      </li>

                      <li>
                        Appointment, booking, and service-related information.
                      </li>

                      <li>
                        Healthcare-provider profile and professional
                        information.
                      </li>

                      <li>
                        Technical information such as device, browser,
                        IP-related, and usage information.
                      </li>

                      <li>
                        Information voluntarily submitted through forms, support
                        requests, or other platform features.
                      </li>
                    </ul>
                  </LegalSection>

                  {/* 3 */}

                  <LegalSection title="3. How We Use Information">
                    <p>Information may be used to:</p>

                    <ul>
                      <li>Provide and operate MRHealth services.</li>

                      <li>Manage user accounts.</li>

                      <li>Facilitate appointments and healthcare services.</li>

                      <li>
                        Improve platform functionality and user experience.
                      </li>

                      <li>Maintain platform security.</li>

                      <li>
                        Detect and prevent fraudulent or unauthorized activity.
                      </li>

                      <li>
                        Communicate important service-related information.
                      </li>

                      <li>Meet legal and regulatory obligations.</li>
                    </ul>
                  </LegalSection>

                  {/* 4 */}

                  <LegalSection title="4. Healthcare Information">
                    <p>
                      Healthcare-related information can be particularly
                      sensitive. Where such information is processed through the
                      platform, we aim to handle it only for legitimate purposes
                      connected with providing or facilitating the requested
                      healthcare services and platform functionality, subject to
                      applicable law.
                    </p>
                  </LegalSection>

                  {/* 5 */}

                  <LegalSection title="5. Data Sharing">
                    <p>MRHealth does not sell or rent personal information.</p>

                    <p>
                      Information may be shared with healthcare providers,
                      service providers, technology providers, payment
                      providers, communication providers, or other trusted
                      partners when necessary to provide requested services,
                      maintain the platform, protect users, or comply with
                      applicable legal requirements.
                    </p>
                  </LegalSection>

                  {/* 6 */}

                  <LegalSection title="6. Payment Information">
                    <p>
                      Where payments are processed through third-party payment
                      providers, payment processing may be handled by those
                      providers in accordance with their applicable privacy
                      policies and security practices.
                    </p>

                    <p>
                      MRHealth does not intend to store complete payment-card
                      credentials unless specifically required and lawfully
                      supported for the relevant service.
                    </p>
                  </LegalSection>

                  {/* 7 */}

                  <LegalSection title="7. Data Security">
                    <p>
                      We use reasonable technical and organizational measures
                      intended to protect information against unauthorized
                      access, alteration, disclosure, or destruction.
                    </p>

                    <p>
                      However, no internet-based service can guarantee absolute
                      security.
                    </p>
                  </LegalSection>

                  {/* 8 */}

                  <LegalSection title="8. Cookies and Similar Technologies">
                    <p>
                      MRHealth may use cookies, local storage, session
                      technologies, or similar mechanisms to maintain sessions,
                      remember preferences, improve functionality, and support
                      security.
                    </p>
                  </LegalSection>

                  {/* 9 */}

                  <LegalSection title="9. Data Retention">
                    <p>
                      Information may be retained for as long as reasonably
                      necessary to provide services, maintain business and
                      security records, resolve disputes, enforce agreements, or
                      satisfy applicable legal and regulatory requirements.
                    </p>
                  </LegalSection>

                  {/* 10 */}

                  <LegalSection title="10. User Rights">
                    <p>
                      Subject to applicable law, users may have rights to
                      request access to, correction of, or deletion of certain
                      personal information.
                    </p>

                    <p>
                      Requests may be submitted through the contact channels
                      provided by MRHealth.
                    </p>
                  </LegalSection>

                  {/* 11 */}

                  <LegalSection title="11. Children's Privacy">
                    <p>
                      Users should provide information about children only when
                      they are legally authorized to do so and where the
                      platform's services permit such use.
                    </p>
                  </LegalSection>

                  {/* 12 */}

                  <LegalSection title="12. Policy Updates">
                    <p>
                      This Privacy Policy may be revised from time to time to
                      reflect changes in our services, technology, legal
                      requirements, or privacy practices.
                    </p>
                  </LegalSection>

                  {/* 13 */}

                  <LegalSection title="13. Contact Us">
                    <p>
                      If you have questions, concerns, or requests regarding
                      this Privacy Policy or the handling of your information,
                      please contact MRHealth through the official contact
                      information provided on the platform.
                    </p>
                  </LegalSection>

                  <LegalNotice>
                    By continuing to use MRHealth after this Privacy Policy is
                    updated, you acknowledge the revised policy.
                  </LegalNotice>
                </motion.section>
              )}
            </AnimatePresence>
          </div>

          {/* ======================================================
              FOOTER
          ====================================================== */}

          <div className="border-t border-slate-200 bg-slate-50/70 px-5 sm:px-8 lg:px-12 py-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-slate-500">
              <p>© {new Date().getFullYear()} MRHealth. All rights reserved.</p>

              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600" />

                <span>Your privacy and security matter to us.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SECTION HEADER
// ============================================================

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="border-b border-slate-200 pb-5">
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Icon */}

        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>

        {/* Heading */}

        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {title}
          </h2>

          <p className="mt-1 text-sm sm:text-base text-slate-500">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LEGAL SECTION
// ============================================================

function LegalSection({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
        {title}
      </h3>

      <div className="space-y-3 text-sm sm:text-base text-slate-600 leading-7">
        {React.Children.map(children, (child) => {
          if (!child) {
            return null;
          }

          if (child.type === "ul") {
            return React.cloneElement(child, {
              className:
                "list-disc pl-5 sm:pl-6 space-y-2 marker:text-blue-500",
            });
          }

          return child;
        })}
      </div>
    </div>
  );
}

// ============================================================
// LEGAL NOTICE
// ============================================================

function LegalNotice({ children }) {
  return (
    <div className="mt-8 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 sm:p-5">
      <div className="flex gap-3">
        <ShieldCheck className="flex-shrink-0 w-5 h-5 text-blue-600 mt-0.5" />

        <p className="text-sm sm:text-base text-blue-900 leading-6 font-medium">
          {children}
        </p>
      </div>
    </div>
  );
}
